import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, SafeHtml, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../environments/environment';
import { ApiImagePipe } from '../pipes/image.pipe';
import { MetaModel } from '../models/meta.model';
import { MediaModel } from '../models/media.model';
import { SettingsService } from './settings.service';
import { SettingsModel } from '../models/setting.model';

@Injectable({
  providedIn: 'root',
})
export class MetaService {
  apiDomain = environment.apiDomain;

  jsonLD?: SafeHtml;

  renderer?: Renderer2;

  defaultMedia: MediaModel = new MediaModel();

  settings: SettingsModel = new SettingsModel();

  constructor(
    private meta: Meta,
    private title: Title,
    @Inject(DOCUMENT) private document: Document,
    private settingsService: SettingsService,
    private rendererFactory: RendererFactory2,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.settings = this.settingsService.settings();
  }

  setMeta(title: string, description: string, image: MediaModel, url: string) {
    let clientId = 0;
    let domainInfo = ['', '', ''];
    domainInfo = this.getDomainInfo(this.settings.ClientId);
    clientId = this.settings.ClientId;

    if (this.renderer) {
      const jsonWebsite = this.websiteSchema(domainInfo[0], domainInfo[1]);

      let script = this.renderer.createElement('script');
      script.type = 'application/ld+json';
      script.text = `${JSON.stringify(jsonWebsite)}`;

      this.renderer.appendChild(this.document.body, script);

      const jsonOrg = this.orgSchema(domainInfo[0], domainInfo[1]);
      script = this.renderer.createElement('script');
      script.type = 'application/ld+json';
      script.text = `${JSON.stringify(jsonOrg)}`;

      this.defaultMedia = this.settings.Media ?? new MediaModel();

      this.renderer.appendChild(this.document.body, script);
    }

    this.setTags(title, description, image, url, domainInfo);
  }

  setTags(
    title: string,
    description: string,
    image: MediaModel,
    url: string,
    domainInfo: string[],
  ) {
    const isMobile = this.settingsService.getMobileStatus();
    const imagePipe = new ApiImagePipe(this.settingsService, isMobile);
    const newMetaModel = new MetaModel();
    newMetaModel.Title = title;
    newMetaModel.Description = description;
    newMetaModel.Image = image;
    newMetaModel.Url = url;
    const faviconPath = '/assets/';

    const htmlTag = this.document.querySelector('html');
    if (this.renderer && htmlTag) {
      this.renderer.setAttribute(htmlTag, 'lang', domainInfo[3]);
    }

    const defaultImage = new MediaModel();
    defaultImage.Url =
      this.apiDomain + 'media/crop/850/450/informasjon/Bakgrunn.jpg';
    defaultImage.TypeId = 1;
    defaultImage.PageType = 6;
    defaultImage.ImageRatio = 0.66;

    let imageUrl = '';
    if ((image !== null && image.Url) || image.Url !== '') {
      imageUrl =
        imagePipe.transform(image.Url, 1200, image, 'fill', 0, true) ?? '';
    } else {
      imageUrl = '../../assets/images/Background.jpg';
    }

    this.meta.addTag({ name: 'description', content: description });
    this.title.setTitle(title);

    // Favicons
    this.createLink('shortcut icon', '', faviconPath + 'favicon.ico', '', '');
    this.createLink(
      'icon',
      'image/x-icon',
      faviconPath + 'favicon.ico',
      '',
      '',
    );
    this.createLink(
      'apple-touch-icon',
      '',
      faviconPath + 'favicon.ico',
      '180x180',
      '',
    );
    this.createLink(
      'icon',
      'image/png',
      faviconPath + 'favicon-32x32.png',
      '32x32',
      '',
    );
    this.createLink(
      'icon',
      'image/png',
      faviconPath + 'favicon-16x16.png',
      '16x16',
      '',
    );
    this.createLink(
      'mask-icon',
      '',
      faviconPath + 'safari-pinned-tab.svg',
      '',
      '#5bbad5',
    );
    this.meta.addTag({ name: 'msapplication-TileColor', content: '#2b5797' });
    this.meta.addTag({ name: 'theme-color', content: '#ffffff' });

    // Facebook
    this.meta.addTag({ name: 'og:type', content: 'website' });
    this.meta.addTag({ name: 'og:url', content: url });
    this.meta.addTag({ name: 'og:title', content: title });
    this.meta.addTag({ name: 'og:description', content: description });
    this.meta.addTag({ name: 'og:locale', content: domainInfo[2] });
    this.meta.addTag({
      name: 'og:image',
      content: imageUrl ?? '',
    });

    // Twitter
    this.meta.addTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.addTag({ name: 'twitter:domain', content: domainInfo[0] });
    this.meta.addTag({ name: 'twitter:url', content: url });
    this.meta.addTag({ name: 'twitter:title', content: title });
    this.meta.addTag({ name: 'twitter:description', content: description });
    this.meta.addTag({
      name: 'twitter:image',
      content: imageUrl ?? '',
    });
  }

  getDomainInfo(clientId: number) {
    switch (clientId) {
      case 1:
        return ['https://hotellstockholm.se', 'HotellStockholm', 'sv_SE', 'sv'];
      case 2:
        return ['https://hotell-oslo.no', 'HotellOslo', 'no_NB', 'no'];
      case 3:
        return ['https://bergen-hotell.no', 'BergenHotell', 'no_NB', 'no'];
      case 4:
        return ['https://hotell.no', 'Hotell.no', 'no_NB', 'no'];
      case 5:
        return [
          'https://Hotell-Kristiansand.no',
          'HotellKristiansand',
          'no_NB',
          'no',
        ];
    }

    return ['', ''];
  }

  createLink(
    rel: string,
    type: string,
    href: string,
    sizes: string,
    color: string,
  ) {
    const link: HTMLLinkElement = this.document.createElement('link');
    link.setAttribute('rel', rel);

    if (type) {
      link.setAttribute('type', type);
    }
    if (href) {
      link.setAttribute('href', href);
    }
    if (sizes) {
      link.setAttribute('sizes', sizes);
    }
    if (color) {
      link.setAttribute('color', color);
    }
    this.document.head.appendChild(link);
  }

  websiteSchema = (url?: string, name?: string) => {
    return {
      '@context': 'http://schema.org',
      '@type': 'WebSite',
      url: url,
      name: name,
    };
  };

  orgSchema = (url?: string, name?: string) => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: url,
    name: name,
  });

  setCanonical(clientId: number, canonical: string) {
    const domain = this.getDomainInfo(clientId)[0];
    const link: HTMLLinkElement = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', domain + canonical.toLowerCase());
    this.document.head.appendChild(link);
  }
}
